import { TableSkeleton } from "components/PortalLoading";
import useFetchSubjectGroupStudents from "api/SubjectGroupResource/useFetchSubjectGroupStudents";
import { Column } from "primereact/column";
import { DataTable } from "components/PortalDataTable";
import Dialog from "components/Overlays/ResponsiveDialog";
import { useMemo, useState } from "react";
import { BsSearch } from "react-icons/bs";
import { FiUsers } from "react-icons/fi";
import { Link } from "react-router-dom";

const ViewSubjectGroupStudents = ({ id, name, numberOfStudents }) => {
	const [display, setDisplay] = useState(false);
	const [searchTerm, setSearchTerm] = useState("");

	const { data, isLoading } = useFetchSubjectGroupStudents(id, display);

	const filteredStudents = useMemo(() => {
		const students = data?.students || [];
		const term = searchTerm.trim().toLowerCase();
		if (!term) {
			return students;
		}
		return students.filter((student) => {
			const haystack = `${student.first_name || ""} ${student.last_name || ""} ${
				student.email || ""
			}`.toLowerCase();
			return haystack.includes(term);
		});
	}, [data?.students, searchTerm]);

	const handleHide = () => {
		setDisplay(false);
		setSearchTerm("");
	};

	const nameTemplate = (row) => (
		<Link
			to={`/portal/users/${row.id}?tab=profile`}
			className="text-akgreen hover:underline"
		>
			{row.first_name}
		</Link>
	);

	const lastNameTemplate = (row) => (
		<Link
			to={`/portal/users/${row.id}?tab=profile`}
			className="text-akgreen hover:underline"
		>
			{row.last_name}
		</Link>
	);

	return (
		<>
			<button
				type="button"
				className="px-2 py-1 rounded-full bg-gray-100 text-gray-600 text-xs hover:bg-gray-200 cursor-pointer border-0"
				onClick={() => setDisplay(true)}
				title="View students in this group"
			>
				{numberOfStudents} students
			</button>

			<Dialog
				visible={display}
				style={{ width: "60vw" }}
				header={
					<div className="text-xl font-sans font-bold sticky top-4 z-20 bg-white mr-2">
						<p>Students in {name}</p>
						<p className="text-sm font-normal text-gray-500 mt-1">
							{data?.assigned_users_count ?? numberOfStudents} student
							{(data?.assigned_users_count ?? numberOfStudents) === 1 ? "" : "s"} assigned
						</p>
					</div>
				}
				onHide={handleHide}
				position="top"
				draggable={false}
				contentClassName="rounded-b-xl"
				className="rounded-xl relative"
			>
				<div className="space-y-4 py-2">
					<div className="relative">
						<BsSearch
							className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
							size={16}
						/>
						<input
							type="text"
							placeholder="Search by name or email..."
							value={searchTerm}
							onChange={(e) => setSearchTerm(e.target.value)}
							className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-akgreen"
						/>
					</div>

					{isLoading ? (
						<TableSkeleton label="Loading students…" columns={[{ label: "Name", kind: "person" }, "Email", "Actions"]} rows={3} footer={false} />
					) : filteredStudents.length === 0 ? (
						<div className="flex flex-col items-center justify-center py-12 text-gray-400">
							<FiUsers size={48} className="mb-3" />
							<p className="text-sm">
								{searchTerm
									? "No students match your search"
									: "No students assigned to this group"}
							</p>
						</div>
					) : (
						<DataTable
							value={filteredStudents}
							tableClassName="font-sans text-sm"
							autoLayout
							stripedRows
						>
							<Column
								field="first_name"
								header="First Name"
								sortable
								body={nameTemplate}
							/>
							<Column
								field="last_name"
								header="Last Name"
								sortable
								body={lastNameTemplate}
							/>
							<Column field="email" header="Email" sortable />
						</DataTable>
					)}
				</div>
			</Dialog>
		</>
	);
};

export default ViewSubjectGroupStudents;
